import { collectionApplicationReview } from '@/lib/collection-application-evidence'
/**
 * lib/collection-membership.ts
 *
 * PURE in-memory predicate `productMatchesSlug(product, slug): boolean` that
 * faithfully mirrors the collection-membership logic in
 * `app/api/collections/[slug]/route.ts`.
 *
 * Built for the Phase-0 classification-migration verification harness
 * (scripts/poc-collection-delta.ts). It lets us compute, per collection slug,
 * exactly which active products would be ADDED or REMOVED when the catalog is
 * re-classified — WITHOUT touching the live route or the database.
 *
 * GROUND TRUTH: app/api/collections/[slug]/route.ts. Every branch here
 * corresponds to one query branch in that file; the post-fetch validator
 * mirrors `buildIsValidProduct` (route.ts lines 312-354). SQL semantics are
 * modeled precisely:
 *   - `type.ilike.%X%`        → case-insensitive substring of products.type
 *   - `type.eq.X`             → case-insensitive exact match of products.type
 *                               (Supabase text comparison; .eq on a citext-ish
 *                               column is case-sensitive, but products.type
 *                               values are already lowercase canonical slugs,
 *                               so case folding is a harmless no-op that also
 *                               guards against stray casing)
 *   - `tags.ov.{...}`         → array intersection non-empty
 *   - `tags.cs.{X}`           → tags array contains X
 *   - `form_factor.cs.{X}`    → form_factor array contains X
 *   - `.in('type', [...])`    → exact membership
 *   - `.contains('tags',[X])` → tags array contains X
 * Null arrays are treated as empty.
 *
 * REUSE: all collection→facet mappings come from lib/collection-filters.ts and
 * lib/collection-url-parser.ts. Only the COMBINATION glue (branch dispatch +
 * predicate evaluation) and the route-local regex constants are defined here.
 * The regex arrays are copied verbatim from route.ts (they are declared inside
 * the GET handler there and are not exported, so they must be inlined — this is
 * the one place fidelity depends on a manual copy; keep in sync with route.ts
 * lines 205-310).
 *
 * NOTE ON FIDELITY SCOPE: this predicate assumes the caller has already
 * restricted the input set to status='active' AND pdp_eligible=true (the
 * harness SELECT does exactly that). The `.eq('status','active')` /
 * `.eq('pdp_eligible', true)` guards present in every route branch are
 * therefore not re-checked here — feeding a non-eligible row would be a caller
 * error. This keeps the predicate focused on the membership facets that the
 * migration actually changes.
 */

import {
  TYPE_TO_DB,
  TITLE_SEARCH,
  WIDTH_RANGES,
  CAPACITY_RANGES,
  BOOLEAN_COLLECTIONS,
  COLOR_TO_DB_PATTERNS,
  colorMatchesCollection,
  SPECIALTY_TYPES,
  FILTER_TYPES,
  getDbTypesForSlug,
  getTitleSearchTerms,
  getAllowedTypesForSlug,
  getCategoryScopeForSlug,
  getFormFactorCollection,
  getCollectionTitleExcludesForSlug,
  getMaxCuftForSlug,
  extractMaxCuftFromTitle,
  getUseCaseTags,
} from '@/lib/collection-filters';
import { parseCollectionUrl, ParsedCollectionUrl } from '@/lib/collection-url-parser';
// FRIDGE-401 Step 4 find: true parts are never collection members — see the
// unconditional TRUE_PART_TYPES block in the validator below, mirroring
// route.ts and lib/collection-page-data.ts.
import { TRUE_PART_TYPES } from '@/lib/parts/fpn';

// ============================================================================
// Product shape — the subset of `products` columns the predicate reads.
// ============================================================================
export interface MembershipProduct {
  id?: string;
  brand?: string | null;
  title?: string | null;
  type?: string | null;
  category?: string | null;
  form_factor?: string[] | null;
  tags?: string[] | null;
  features?: string[] | null;
  images?: unknown[] | null;
  width_in?: number | null;
  capacity_cuft?: number | null;
  color?: string | null;
  is_commercial?: boolean | null;
  lowest_price_cents?: number | null;
  // canonical_url IS load-bearing: every route branch ends with
  // `validate(p) && p.canonical_url`, and ~19 active+pdp_eligible rows have a
  // NULL canonical_url (they cannot be linked to a PDP, so live drops them from
  // EVERY collection). The migration never changes canonical_url, so it is a
  // membership constant — but it must still gate the predicate to match live.
  canonical_url?: string | null;
}

// ============================================================================
// Route-local regex constants — copied VERBATIM from
// app/api/collections/[slug]/route.ts (lines 205-310). These are declared
// inside the GET handler and not exported, so they must be inlined. Keep in
// sync with route.ts.
// ============================================================================

// route.ts EXCLUDE_PATTERNS (205-223)
const EXCLUDE_PATTERNS: RegExp[] = [
  /\b(washer|washing\s*machine|dryer|range|oven|cooktop|stove|microwave|dishwasher|dehumidifier|air\s*conditioner|air\s*purifier|purifier)\b/i,
  /\b(ice\s*chest|insulated\s*bag|thermal\s*bag|passive\s*cooling)\b/i,
  /\b(replacement\s*(filter|part|kit|gasket|seal|door|shelf|drawer|bulb|thermostat|control|board|sensor|cartridge|cartridges))\b/i,
  /\b(water\s*filter|air\s*filter|water\s*filtration|filtration\s*system|water\s*filter\s*pitcher)\b/i,
  /\b(filter\s*(basket|box|pad|pads|cartridge|cartridges|element|housing|media))\b/i,
  /\b(pond\s*filter|aquarium\s*filter|pool\s*filter|spa\s*filter)\b/i,
  /\b(juice\s*extractor|juicer|coffee\s*maker|kettle|toaster|blender|food\s*processor)\b/i,
  /\b(hepa)\b/i,
  /\b(wasserfilter|wasserfilterkrug|wasserfilterkanne)/i,
  /\b(fridge\s*(magnet|organizer|lock|thermometer))\b/i,
  /\b(refrigerator\s*(magnet|organizer|lock|thermometer|filter))\b/i,
  /\b(water\s*line|supply\s*line)\b/i,
  /\b(dolly|installation\s*kit|strap|moving\s*tool)\b/i,
];

// route.ts NON_REFRIGERATION_FILTER_PATTERNS (232-290)
const NON_REFRIGERATION_FILTER_PATTERNS: RegExp[] = [
  /\bcoffee[\s-]*(makers?|filters?|holders?|baskets?|machines?|brewers?|side)\b/i,
  /\b(makers?|filters?|holders?|baskets?|machines?|brewers?)[\s-]*(for[\s-]*)?coffee\b/i,
  /\bvacuum[\s-]*(cleaners?|filters?|bags?|robots?|sweepers?)\b/i,
  /\b(robot[\s-]?vacuum|robovac|roboterstaubsauger|staubsauger)\b/i,
  /\bpond[\s-]*(filters?|pumps?|kits?|fountains?|skimmers?)\b/i,
  /\bponds\b/i,
  /\baquarium[\s-]*(filters?|pumps?|tanks?)\b/i,
  /\b(swimming[\s-]*)?pool[\s-]*(filters?|cleaners?|skimmers?|cartridges?|pumps?)\b/i,
  /\binflatable[\s-]*pool\b/i,
  /\b(spa|hot[\s-]*tub|jacuzzi)[\s-]*(filters?|cleaners?|cartridges?|water)\b/i,
  /\bgarden[\s-]*hose\b/i,
  /\bshower[\s-]*(filters?|heads?)\b/i,
  /\b(humidifier|dehumidifier|hvac|furnace|hepa|air[\s-]*conditioner)[\s-]*filters?\b/i,
  /\bhepa[\s-]+filters?\b/i,
  /\bhepa\b/i,
  /\b(cat|pet|dog)[\s-]*(water|litter)[\s-]*(fountains?|filters?|bowls?)\b/i,
  /\bbio[\s-]?balls?\b/i,
  /\bsolar[\s-]*(fountains?|pumps?|ponds?)\b/i,
  /\b(faucet|tap|sink|under[\s-]?sink|countertop|whole[\s-]?house|reverse[\s-]?osmosis|ro)[\s-]*(water[\s-]*)?(filters?|filtration|systems?)\b/i,
  /\bfor[\s-]*(taps?|faucets?|sinks?)\b/i,
  /\bfits[\s-]*(standard[\s-]*)?(faucets?|taps?|sinks?)\b/i,
  /\bkitchen[\s-]*(faucet|sink|tap)\b/i,
  /\b(faucet|tap|sink)[\s-]*(combo|combination)\b/i,
  /\bjuice[\s-]*extractor|juicer\b/i,
  // FRIDGE-1498: not a refrigerator's own "AutoFill Pitcher" (as in lib/collection-safety-patterns.ts).
  /(?<!\bauto[\s-]?fill[\s-]+(?:water[\s-]+)?)\b(water[\s-]*)?(pitchers?|jugs?|kettles?)\b/i,
  /\bwater[\s-]*filter[\s-]*dispensers?\b/i,
  /\b(camping|survival|backpacking|outdoor)[\s-]*(water[\s-]*)?(filters?|purifiers?|purification)\b/i,
  /\bwater[\s-]*purifiers?\b/i,
  /\b(portable|electric|rechargeable)[\s-]*water[\s-]*filters?\b/i,
  /\b(kaffee|kaffeevollautomat|kaffeemaschine|espresso([\s-]*machine)?)\b/i,
  /\b(jura|nivona|saeco|delonghi|de'?longhi|melitta|krups|bunn)\b/i,
  /\bwater[\s-]*bottles?\b/i,
  /\b(rv|marine|boat|camper)[\s-]*(\/[\s-]*\w+[\s-]*)?water[\s-]*filters?\b/i,
  /\bpur[\s-]*(systems?|filters?|faucets?)\b/i,
  /\bcompatible[\s-]*with[\s-]*pur\b/i,
  /\bwasserfilter[\s-]*(krug|kanne)\b/i,
  /\b(theken|counter)[\s-]*wasserfilter\b/i,
  /\bwasserhahn\b/i,
  /\bnon[\s-]?gravity[\s-]*water[\s-]*filters?\b/i,
  /\bcuisinart.{0,40}\b(filter[\s-]+(baskets?|covers?|holders?|panels?|assembly|bowls?|lids?|cups?))\b/i,
  /\bcuisinart.{0,40}\b(stainless[\s-]+steel[\s-]+filter|metal[\s-]+filter|full[\s-]+s\/s[\s-]+filter|permanent[\s-]+(nylon[\s-]+)?filter|nylon[\s-]+filter|scale[\s-]+filter|spout[\s-]+filter|tank[\s-]+filter|reusable[\s-]+filter|carafe[\s-]+lid[\s-]+filter|homebarista)\b/i,
  /\bcuisinart.{0,40}\b(filter[\s-]+holder|water[\s-]+filter[\s-]+holder|charcoal[\s-]+water[\s-]+filter[\s-]+holder)\b/i,
  /\bnyl(on)?[\s-]+filter[\s-]+basket\b/i,
  /\bwhole[\s-]?house[\s-]?(pre[\s-]?)?(filter|filtration|system|sediment|pre.filter)/i,
  /\bspin[\s-]?down[\s-]+sediment[\s-]+filter\b/i,
  /\bsediment[\s-]+(pre[\s-]?)?filter\b/i,
];

// route.ts FILTER_ONLY_EXCLUDE_PATTERNS (295-310)
const FILTER_ONLY_EXCLUDE_PATTERNS: RegExp[] = [
  /\b(commercial[\s-]*)?ice[\s-]*(makers?|machines?)\b/i,
  /\bair[\s-]*purifiers?\b/i,
  /\bpurifiers?\b/i,
  /\bfoam[\s-]*(filters?|management|disks?|pads?)\b/i,
  /\b(sink|kitchen)[\s-]*extender\b/i,
  /\baerator\b/i,
  /\bfountain\b/i,
  /\b(faucet|tap)[\s-]*(extender|mount)\b/i,
  /\bfaucet[\s-]*and[\s-]*water[\s-]*filter\b/i,
  /\bcadr\b/i,
  /\b(berkey)\b/i,
  /\bjuice[\s-]*filter\b/i,
  /\b(animal|pet|cat|dog)[\s-]*water[\s-]*(dispenser|fountain|bowl)\b/i,
  /\bpool[\s-]*robot\b/i,
];

// ============================================================================
// SQL-semantics helpers (null arrays treated as empty).
// ============================================================================
function asArr(v: unknown): string[] {
  return Array.isArray(v) ? (v as unknown[]).map((x) => String(x)) : [];
}

/** `type.ilike.%needle%` — case-insensitive substring. */
function typeIlikeContains(type: string | null | undefined, needle: string): boolean {
  if (!type) return false;
  return type.toLowerCase().includes(needle.toLowerCase());
}

/** `type.eq.X` — exact match. products.type is canonical lowercase, so fold case. */
function typeEq(type: string | null | undefined, value: string): boolean {
  if (!type) return false;
  return type.toLowerCase() === value.toLowerCase();
}

/** `tags.ov.{a,b,c}` — array intersection non-empty. */
function arrOverlaps(arr: string[] | null | undefined, candidates: string[]): boolean {
  const set = new Set(asArr(arr));
  return candidates.some((c) => set.has(c));
}

/** `tags.cs.{X}` / `form_factor.cs.{X}` / `.contains(col,[X])` — array contains X. */
function arrContains(arr: string[] | null | undefined, value: string): boolean {
  return asArr(arr).includes(value);
}

/** route.ts extractCapacityFromTitle (lines 1139-1146 / collection-filters export). */
function extractCapacityFromTitle(title: string): number | null {
  const match = title.match(/(\d+\.?\d*)\s*(cu\.?\s*ft\.?|cf)/i);
  if (match) return parseFloat(match[1]);
  return null;
}

// route.ts:1534-1554 — fallback type whitelist for title-only style collections.
const FALLBACK_ALLOWED_TYPES: string[] = [
  'refrigerator',
  'french-door',
  'side-by-side',
  'top-freezer',
  'bottom-freezer',
  'compact',
  'counter-depth',
  'wine-cooler',
  'beverage-cooler',
  'kegerator',
  'ice-maker',
  'built-in',
  'cooler',
  'freezer',
  'upright-freezer',
  'chest-freezer',
  'compact-freezer',
];

// Full-size refrigerator types excluded by several title-branch special cases
// (route.ts:1626, 1642, 1648).
const FULL_SIZE_FRIDGE_TYPES = [
  'french-door',
  'side-by-side',
  'top-freezer',
  'bottom-freezer',
  'refrigerator',
];

const BEVERAGE_COLLECTIONS = [
  'beer-fridges',
  'beverage-fridges',
  'beverage-coolers',
  'beverage-centers',
];

// ============================================================================
// POST-FETCH VALIDATOR — mirrors buildIsValidProduct (route.ts 312-350).
//
// Returns the predicate function for a given (collectionDbTypes, slug). The
// caller invokes route's `validate(p) && p.canonical_url`; canonical_url is a
// membership constant for pdp_eligible rows (see header), so omitted.
// ============================================================================
function buildIsValidProduct(
  collectionDbTypes: string[] = [],
  slugForTitleExcludes?: string,
): (p: MembershipProduct) => boolean {
  const isFilterCollection = collectionDbTypes.some((t) =>
    (FILTER_TYPES as readonly string[]).includes(t),
  );
  const titleExcludes = slugForTitleExcludes
    ? getCollectionTitleExcludesForSlug(slugForTitleExcludes)
    : [];
  const maxCuft = slugForTitleExcludes ? getMaxCuftForSlug(slugForTitleExcludes) : null;
  const categoryScope = slugForTitleExcludes
    ? getCategoryScopeForSlug(slugForTitleExcludes)
    : null;

  return (p: MembershipProduct) => {
    const t = (p.title || '').toLowerCase();
    const brand = (p.brand || '').toLowerCase();
    // route.ts:328
    if (brand === 'generic') return false;
    // route.ts:329 — category scope gates ONLY when product.category present.
    if (categoryScope && p.category && !categoryScope.includes(p.category as never)) return false;
    // route.ts:330 — images required.
    if (!p.images || (Array.isArray(p.images) && p.images.length === 0)) return false;
    // route.ts:331 — FILTER_TYPES block on non-filter collections.
    if ((FILTER_TYPES as readonly string[]).includes(p.type as string) && !isFilterCollection) {
      return false;
    }
    // route.ts — true parts (FPN family: gasket · sensor · part) are never
    // collection members, on ANY collection (FRIDGE-401 Step 4 find).
    if ((TRUE_PART_TYPES as readonly string[]).includes(p.type as string)) return false;
    // route.ts:332 — NON_REFRIGERATION patterns (every collection).
    if (NON_REFRIGERATION_FILTER_PATTERNS.some((re) => re.test(t))) return false;
    // route.ts:334 — FILTER_ONLY patterns (filter collections only).
    if (isFilterCollection && FILTER_ONLY_EXCLUDE_PATTERNS.some((re) => re.test(t))) return false;
    // route.ts:338 — per-collection title excludes.
    if (titleExcludes.length > 0 && titleExcludes.some((re) => re.test(t))) return false;
    // route.ts:341-344 — capacity cap.
    if (maxCuft != null) {
      const cuft = extractMaxCuftFromTitle(p.title || '');
      if (cuft != null && cuft > maxCuft) return false;
    }
    // route.ts:347-348 — skip EXCLUDE_PATTERNS on filter collections; else apply.
    if (isFilterCollection) return true;
    return !EXCLUDE_PATTERNS.some((re) => re.test(t));
  };
}

// ============================================================================
// slugCanonical resolution — mirrors route.ts 654-731, MINUS the live DB
// lookups (the `collections` table check and the brand-existence query). Those
// only ever SET slugCanonical = slugInput or mark isBrandOnly; for the
// registry slugs the harness iterates (all recognized type/title/boolean/
// width/capacity/form-factor slugs), the in-memory resolution below reproduces
// the same slugCanonical. Brand-prefixed and brand-only slugs are not part of
// the migration registry, so their DB-dependent paths are intentionally not
// modeled (a non-brand registry slug never takes them).
// ============================================================================
function resolveBranchContext(slugInput: string): {
  parsed: ParsedCollectionUrl;
  slugCanonical: string;
} {
  const parsed = parseCollectionUrl(slugInput);
  // route.ts initializes slugCanonical = parsed.typeSlug (656), then a series
  // of fallbacks. For registry slugs the relevant outcome is: a recognized
  // standalone slug resolves to itself. We therefore prefer the input slug when
  // it is itself a recognized collection key, matching route's FIRST/SECOND/
  // SIXTH/SEVENTH resolution for non-brand inputs.
  let slugCanonical: string | null = parsed.typeSlug;

  const isRecognized =
    BOOLEAN_COLLECTIONS[slugInput] != null ||
    TYPE_TO_DB[slugInput] != null ||
    TITLE_SEARCH[slugInput] != null ||
    WIDTH_RANGES[slugInput] != null ||
    CAPACITY_RANGES[slugInput] != null ||
    getFormFactorCollection(slugInput) != null;

  // route.ts SECOND (678): a known standalone collection slug resolves to
  // itself (takes precedence over a parser-extracted typeSlug suffix).
  if (isRecognized) {
    slugCanonical = slugInput;
  }
  // route.ts SEVENTH (729): fallback to input.
  if (!slugCanonical) slugCanonical = slugInput;

  return { parsed, slugCanonical };
}

// ============================================================================
// width/capacity config resolution — mirrors route.ts 768-787, including the
// `slugCanonical.startsWith(prefix)` fallback for combined slugs.
// ============================================================================
function resolveWidthConfig(slugCanonical: string): { min: number; max: number } | null {
  let widthConfig = WIDTH_RANGES[slugCanonical] || null;
  if (!widthConfig) {
    for (const [widthSlug, config] of Object.entries(WIDTH_RANGES)) {
      if (slugCanonical.startsWith(widthSlug)) {
        widthConfig = config;
        break;
      }
    }
  }
  return widthConfig;
}

function resolveCapacityConfig(
  slugCanonical: string,
  widthConfig: { min: number; max: number } | null,
): { min: number; max: number } | null {
  let capacityConfig = CAPACITY_RANGES[slugCanonical] || null;
  // route.ts only probes the capacity prefix fallback when NO width config was
  // found (768-787: the startsWith loops are nested under `if (!widthConfig &&
  // !capacityConfig)` and capacity is only tried `if (!widthConfig)`).
  if (!capacityConfig && !widthConfig) {
    for (const [capSlug, config] of Object.entries(CAPACITY_RANGES)) {
      if (slugCanonical.startsWith(capSlug)) {
        capacityConfig = config;
        break;
      }
    }
  }
  return capacityConfig;
}

// ============================================================================
// BRANCH A — FILTERED COLLECTION (route.ts 365-651).
// ============================================================================
function matchFiltered(p: MembershipProduct, parsed: ParsedCollectionUrl, slugInput: string): boolean {
  const t = (p.title || '').toLowerCase();

  // route.ts:409-411 — Decision-12 commercial filter (non-commercial slugs).
  if (!parsed.typeSlug?.includes('commercial')) {
    if (p.is_commercial === true) return false; // is_commercial IS NULL or =false passes
  }
  // route.ts:414-416 — brand filter (substring, %name%).
  if (parsed.brandSlug && parsed.brandName) {
    if (!(p.brand || '').toLowerCase().includes(parsed.brandName.toLowerCase())) return false;
  }

  const widthRange = parsed.typeSlug ? WIDTH_RANGES[parsed.typeSlug] : null;
  const capacityRange = parsed.typeSlug ? CAPACITY_RANGES[parsed.typeSlug] : null;

  if (widthRange) {
    // route.ts:419-423
    if (p.category !== 'refrigerator') return false;
    if (p.width_in == null || p.width_in < widthRange.min || p.width_in > widthRange.max) {
      return false;
    }
  } else if (parsed.typeSlug && !capacityRange) {
    // route.ts:425-504 — type filter with OR-group.
    if (!matchTypeWithFilters(p, parsed.typeSlug)) return false;
  } else if (capacityRange) {
    // route.ts:505-508 + post-filter 566-580
    if (p.category !== 'refrigerator') return false;
    let capacity: number | null | undefined = p.capacity_cuft;
    if (capacity === null || capacity === undefined) {
      capacity = extractCapacityFromTitle(p.title || '');
    }
    if (capacity === null || capacity === undefined) return false;
    if (!(capacity >= capacityRange.min && capacity < capacityRange.max)) return false;
  }

  // route.ts:511-522 — color filter (color column OR title).
  if (parsed.colorSlug) {
    // One rule with the count script: brand stripped from the title, and
    // "stainless" never matching black stainless (owner 09/03/2026 visual audit).
    if (!colorMatchesCollection(parsed.colorSlug, p.color, p.title, p.brand)) return false;
  }

  // route.ts:525-528 — price filter.
  if (parsed.priceMaxCents) {
    if (p.lowest_price_cents == null) return false;
    if (!(p.lowest_price_cents < parsed.priceMaxCents && p.lowest_price_cents > 0)) return false;
  }

  // route.ts:532-534 — energy-star (AND tags contains energy-star).
  if (parsed.isEnergyStar && slugInput !== 'energy-star-specialty-appliances') {
    if (!arrContains(p.tags, 'energy-star')) return false;
  }

  // FRIDGE-259 item 2 — use-case facet: tag overlap when the use case maps to
  // live for-* tags (USE_CASE_TAG_MAP); an unmapped use case applies no
  // predicate, the pre-wiring SEO/display-only behavior. Mirrors the same
  // clause in lib/collection-page-data.ts, route.ts and the count-script.
  if (parsed.useCaseSlug) {
    const useCaseTags = getUseCaseTags(parsed.useCaseSlug);
    if (useCaseTags && !arrOverlaps(p.tags, useCaseTags)) return false;
  }

  // route.ts:543-545 — commercial exclusion (non-commercial slugs).
  if (!parsed.typeSlug?.includes('commercial')) {
    if (arrContains(p.tags, 'commercial')) return false;
  }

  // route.ts:586 — post-validator scoped to typeSlug.
  const validatorDbTypes = parsed.typeSlug ? getDbTypesForSlug(parsed.typeSlug) : [];
  const validate = buildIsValidProduct(validatorDbTypes, parsed.typeSlug || slugInput);
  return validate(p);
}

/** route.ts:425-504 — the type-with-filters OR-group (used inside BRANCH A). */
function matchTypeWithFilters(p: MembershipProduct, typeSlug: string): boolean {
  const dbTypes = getDbTypesForSlug(typeSlug);
  const titleSearchTerms = getTitleSearchTerms(typeSlug);
  const booleanConfig = BOOLEAN_COLLECTIONS[typeSlug];
  const formFactorCfg = getFormFactorCollection(typeSlug);

  const isFilterCollection = dbTypes.some((t) => (FILTER_TYPES as readonly string[]).includes(t));
  // route.ts:434-436 — FILTER_TYPES exclusion.
  if (!isFilterCollection) {
    if ((FILTER_TYPES as readonly string[]).includes(p.type as string)) return false;
  }
  // route.ts:438-441 — allowed-types whitelist.
  const allowedTypes = getAllowedTypesForSlug(typeSlug);
  if (allowedTypes && allowedTypes.length > 0) {
    if (!allowedTypes.includes(p.type as string)) return false;
  }
  // route.ts:446-460 — family scope (booleanConfig).
  if (booleanConfig) {
    if (booleanConfig.category) {
      if (!booleanConfig.category.includes(p.category as never)) return false;
    } else {
      // .eq(booleanConfig.field, true) — only is_commercial reaches here.
      if (booleanConfig.field === 'is_commercial') {
        if (p.is_commercial !== true) return false;
      } else {
        // Retired is_refrigerator/is_freezer/is_specialty columns are dropped;
        // the route never reaches this for category-backed configs. Defensive.
        return false;
      }
    }
    if (booleanConfig.excludeCommercial) {
      if (p.is_commercial === true) return false;
    }
  }
  // route.ts:466-468 — form-factor scope.
  if (formFactorCfg) {
    if (p.category !== formFactorCfg.family) return false;
  }

  // route.ts:471-504 — OR-group: type substring OR tags overlap OR form_factor
  // contains OR title substring.
  const conditions: boolean[] = [];
  if (dbTypes.length > 0) {
    const typeMatch = dbTypes.some((dt) => typeIlikeContains(p.type, dt));
    const tagMatch = arrOverlaps(p.tags, dbTypes);
    conditions.push(typeMatch);
    conditions.push(tagMatch);
  }
  if (formFactorCfg) {
    for (const ff of formFactorCfg.formFactors) {
      conditions.push(arrContains(p.form_factor, ff));
    }
  }
  if (titleSearchTerms && titleSearchTerms.length > 0 && !isFilterCollection) {
    const titleMatch = titleSearchTerms.some((term) => typeIlikeContains(p.title, term));
    conditions.push(titleMatch);
  }
  if (conditions.length > 0) {
    return conditions.some(Boolean);
  }
  // route.ts:502-503 fallback OR (type substring of slug OR tags contains slug).
  return typeIlikeContains(p.type, typeSlug) || arrContains(p.tags, typeSlug);
}

// ============================================================================
// BRANCH B — WIDTH-BASED (route.ts 1060-1134).
// ============================================================================
function matchWidth(p: MembershipProduct, widthConfig: { min: number; max: number }): boolean {
  if (p.category !== 'refrigerator') return false;
  if (p.width_in == null || p.width_in < widthConfig.min || p.width_in > widthConfig.max) {
    return false;
  }
  if (arrContains(p.tags, 'commercial')) return false; // route.ts:1089
  const validate = buildIsValidProduct([], slugForWidthCapacityValidator);
  return validate(p);
}

// ============================================================================
// BRANCH C — CAPACITY-BASED (route.ts 1137-1233).
// ============================================================================
function matchCapacity(p: MembershipProduct, capacityConfig: { min: number; max: number }): boolean {
  if (p.category !== 'refrigerator') return false; // route.ts:1173
  if (arrContains(p.tags, 'commercial')) return false; // route.ts:1174
  // route.ts:1186-1201 — validator runs INSIDE the capacity filter, before the
  // capacity range check. Order matters only for short-circuit, not result.
  const validate = buildIsValidProduct([], slugForWidthCapacityValidator);
  if (!validate(p)) return false;
  let capacity: number | null | undefined = p.capacity_cuft;
  if (capacity === null || capacity === undefined) {
    capacity = extractCapacityFromTitle(p.title || '');
  }
  if (capacity === null || capacity === undefined) return false;
  return capacity >= capacityConfig.min && capacity < capacityConfig.max;
}

// Width/capacity branches in route.ts call the DEFAULT validator
// `isValidProduct = buildIsValidProduct([], slugInput)` (route.ts:354,
// 1102/1188). slugInput is the raw URL slug. For a width/capacity registry slug
// the raw slug === slugCanonical, and getCollectionTitleExcludesForSlug /
// getMaxCuftForSlug / getCategoryScopeForSlug all key on it. We pass that slug
// through this module-level handoff (set per-call in productMatchesSlug).
let slugForWidthCapacityValidator = '';

// ============================================================================
// BRANCH D — BOOLEAN-BASED (route.ts 1235-1345).
// ============================================================================
function matchBoolean(
  p: MembershipProduct,
  booleanConfig: { field: string; excludeCommercial: boolean; category?: string[] },
  slugCanonical: string,
): boolean {
  // route.ts:1266-1270 — family scope.
  if (booleanConfig.category) {
    if (!booleanConfig.category.includes(p.category as string)) return false;
  } else {
    if (booleanConfig.field === 'is_commercial') {
      if (p.is_commercial !== true) return false;
    } else {
      return false; // retired boolean columns dropped; defensive
    }
  }
  // route.ts:1274-1276 — commercial exclusion (.eq('is_commercial', false)).
  // NOTE: route uses .eq('is_commercial', false) here (STRICTER than the
  // null-or-false form), so a NULL is_commercial is EXCLUDED in this branch.
  if (booleanConfig.excludeCommercial) {
    if (p.is_commercial !== false) return false;
  }
  // route.ts:1280-1282 — specialty only includes SPECIALTY_TYPES.
  if (slugCanonical === 'specialty') {
    if (!(SPECIALTY_TYPES as readonly string[]).includes(p.type as string)) return false;
  }
  // route.ts:1285-1287 — refrigerators/freezers exclude specialty types.
  if (slugCanonical === 'refrigerators' || slugCanonical === 'freezers') {
    if ((SPECIALTY_TYPES as readonly string[]).includes(p.type as string)) return false;
  }
  // route.ts:1300 — validator scoped to slugCanonical, dbTypes=[].
  const validate = buildIsValidProduct([], slugCanonical);
  return validate(p);
}

// ============================================================================
// BRANCH E — HASDBTYPE (route.ts 1419-1527).
// ============================================================================
function matchHasDbType(p: MembershipProduct, slugCanonical: string, dbTypes: string[]): boolean {
  // route.ts:1450-1467 — type.eq OR tags overlap OR form_factor contains
  // (beverage = type only). Family is scoped post-fetch by the validator's
  // categoryScope; route deliberately omits a SQL .eq('category', family) here
  // (it would drop NULL-category rows), so the harness must NOT hard-filter on
  // category!==family either — keep this additive.
  const isBeverageCollection = BEVERAGE_COLLECTIONS.includes(slugCanonical);
  const formFactorCfg = getFormFactorCollection(slugCanonical);
  const typeMatch = dbTypes.some((dt) => typeEq(p.type, dt));
  let matched = typeMatch;
  if (!isBeverageCollection) {
    matched = matched || arrOverlaps(p.tags, dbTypes);
    if (formFactorCfg) {
      matched = matched || formFactorCfg.formFactors.some((ff) => arrContains(p.form_factor, ff));
    }
  }
  if (!matched) return false;
  // route.ts:1465 — commercial exclusion.
  if (arrContains(p.tags, 'commercial')) return false;
  // route.ts:1470-1473 — FILTER_TYPES exclusion.
  const isFilterCollection = dbTypes.some((t) => (FILTER_TYPES as readonly string[]).includes(t));
  if (!isFilterCollection && (FILTER_TYPES as readonly string[]).includes(p.type as string)) {
    return false;
  }
  // route.ts:1475-1478 — allowed-types whitelist (slugCanonical then slugInput;
  // for a registry slug both are equal, so the canonical lookup suffices).
  const allowedTypes = getAllowedTypesForSlug(slugCanonical);
  if (allowedTypes && allowedTypes.length > 0) {
    if (!allowedTypes.includes(p.type as string)) return false;
  }
  // route.ts:1493 — validator scoped to dbTypes + slugCanonical.
  const validate = buildIsValidProduct(dbTypes, slugCanonical);
  return validate(p);
}

// ============================================================================
// BRANCH F — TITLE SEARCH (route.ts 1529-1747).
// ============================================================================
function matchTitleSearch(p: MembershipProduct, slugCanonical: string, slugInput: string): boolean {
  const titleSearchTerms = getTitleSearchTerms(slugCanonical) ?? getTitleSearchTerms(slugInput);
  if (!titleSearchTerms) return false;
  const t = (p.title || '').toLowerCase();
  const dbTypes = getDbTypesForSlug(slugCanonical);
  const effectiveTypes = dbTypes.length > 0 ? dbTypes : FALLBACK_ALLOWED_TYPES;
  const isFeatureBasedCollection = dbTypes.length > 0 && titleSearchTerms.length > 0;
  const formFactorCfg = getFormFactorCollection(slugCanonical) ?? getFormFactorCollection(slugInput);

  // route.ts:1597 — .in('type', effectiveTypes).
  if (!effectiveTypes.includes(p.type as string)) return false;

  // route.ts:1602-1620 — (title OR form_factor) OR-group (non-feature-based only).
  // form_factor is added for form-factor collections; family is scoped post-fetch
  // by the validator's categoryScope (no SQL .eq('category', family) — additive).
  if (!isFeatureBasedCollection && titleSearchTerms.length > 0) {
    const titleMatch = titleSearchTerms.some((term) => typeIlikeContains(p.title, term));
    const ffMatch = formFactorCfg
      ? formFactorCfg.formFactors.some((ff) => arrContains(p.form_factor, ff))
      : false;
    if (!titleMatch && !ffMatch) return false;
  }

  // route.ts:1608-1610 — commercial exclusion (non-commercial collections).
  const isCommercialCollection = slugCanonical.includes('commercial');
  if (!isCommercialCollection) {
    if (arrContains(p.tags, 'commercial')) return false;
  }

  // route.ts:1613-1679 — special-case category/type filters. NOTE: in route
  // these are INDEPENDENT `if` blocks (not else-if); only one ever fires per
  // slug given the disjoint slug sets, so sequential ifs are equivalent.
  if (slugCanonical === 'commercial-freezers') {
    if (p.category !== 'freezer') return false;
  }
  if (slugCanonical === 'commercial-refrigerators') {
    if (p.category !== 'refrigerator') return false;
  }
  if (slugCanonical === 'mini-freezers' || slugCanonical === 'compact-freezers') {
    if (p.category !== 'freezer') return false;
    if (FULL_SIZE_FRIDGE_TYPES.includes(p.type as string)) return false;
  }
  if (slugCanonical === 'small-chest-freezers') {
    if (p.category !== 'freezer') return false;
    // route comment says capacity post-processing, but no capacity filter is
    // actually applied in route.ts for this slug — category gate is the only
    // extra constraint. Mirror route's ACTUAL behavior (no capacity filter).
  }
  if (slugCanonical === 'freezerless-refrigerators') {
    if (p.category !== 'refrigerator') return false;
  }
  if (slugCanonical === 'back-bar-coolers') {
    if (FULL_SIZE_FRIDGE_TYPES.includes(p.type as string)) return false;
  }
  if (BEVERAGE_COLLECTIONS.includes(slugCanonical)) {
    if (FULL_SIZE_FRIDGE_TYPES.includes(p.type as string)) return false;
  }
  if (slugCanonical === 'drawer-freezers' || slugCanonical === 'double-drawer-freezers') {
    if (p.category !== 'freezer') return false;
  }
  if (slugCanonical === 'outdoor-freezers') {
    if (p.category !== 'freezer') return false;
  }
  if (['rv-freezers', 'portable-freezers', 'column-freezers'].includes(slugCanonical)) {
    if (p.category !== 'freezer') return false;
  }
  if (['rv-refrigerators', 'column-refrigerators', 'drawer-refrigerators'].includes(slugCanonical)) {
    if (p.category !== 'refrigerator') return false;
  }
  if (['built-in-wine-coolers', 'dual-zone-wine-coolers', 'single-zone-wine-coolers'].includes(slugCanonical)) {
    if (!['wine-cooler', 'wine-fridge'].includes(p.type as string)) return false;
  }
  // route.ts:1677 — energy-star-specialty keyed on slugInput.
  if (slugInput === 'energy-star-specialty-appliances') {
    if (!(SPECIALTY_TYPES as readonly string[]).includes(p.type as string)) return false;
  }

  // route.ts:1694 — validator scoped to effectiveTypes + slugCanonical.
  const validate = buildIsValidProduct(effectiveTypes, slugCanonical || slugInput);
  if (!validate(p)) return false;

  // route.ts:1700-1713 — feature-based post-filter (title + features).
  if (isFeatureBasedCollection && titleSearchTerms.length > 0) {
    const featuresStr = Array.isArray(p.features)
      ? p.features.map((f) => String(f).toLowerCase()).join(' ')
      : '';
    const searchableText = `${t} ${featuresStr}`;
    const match = titleSearchTerms.some((term) => searchableText.includes(term.toLowerCase()));
    if (!match) return false;
  }

  return true;
}

// ============================================================================
// PUBLIC API — productMatchesSlug.
//
// Dispatch order mirrors route.ts exactly:
//   FILTERED (365) → WIDTH (1060) → CAPACITY (1137) → BOOLEAN (1235)
//   → [BRAND-ONLY (1347): not modeled for registry slugs] → HASDBTYPE (1419)
//   → TITLE-SEARCH (1529).
//
// Important ordering nuance: route computes widthConfig/capacityConfig from
// slugCanonical (768-787) and the boolean/hasDbTypes/title branches are an
// if/else-if CHAIN (1060/1137/1235/1347/1419/1529). So a slug that is BOTH a
// boolean collection AND has dbTypes (none currently do) would take the boolean
// branch. We replicate the same precedence.
// ============================================================================
export function productMatchesSlug(product: MembershipProduct, slug: string): boolean {
  if (collectionApplicationReview(slug)) return false;
  // Universal `&& p.canonical_url` post-check applied identically by EVERY route
  // branch (route.ts 587, 1102, 1188, 1300, 1383, 1494, 1695). A row with no
  // canonical_url is dropped from every collection. Gate once up front.
  if (!product.canonical_url) return false;

  const { parsed, slugCanonical } = resolveBranchContext(slug);

  // BRANCH A — FILTERED.
  if (parsed.isFiltered && (parsed.priceSlug || parsed.colorSlug || parsed.useCaseSlug || parsed.isEnergyStar)) {
    return matchFiltered(product, parsed, slug);
  }

  const widthConfig = resolveWidthConfig(slugCanonical);
  const capacityConfig = resolveCapacityConfig(slugCanonical, widthConfig);

  const dbTypes = getDbTypesForSlug(slugCanonical);
  const titleSearchTerms = getTitleSearchTerms(slugCanonical) ?? getTitleSearchTerms(slug);
  const booleanConfig = BOOLEAN_COLLECTIONS[slugCanonical] || null;

  // BRANCH B — WIDTH.
  if (widthConfig) {
    // route's width/capacity branches use the DEFAULT validator keyed on
    // slugInput (the raw slug). Hand it through the module-level slot.
    slugForWidthCapacityValidator = slug;
    return matchWidth(product, widthConfig);
  }
  // BRANCH C — CAPACITY.
  if (capacityConfig) {
    slugForWidthCapacityValidator = slug;
    return matchCapacity(product, capacityConfig);
  }
  // BRANCH D — BOOLEAN.
  if (booleanConfig) {
    return matchBoolean(product, booleanConfig, slugCanonical);
  }
  // BRANCH E — HASDBTYPE.
  if (dbTypes.length > 0) {
    return matchHasDbType(product, slugCanonical, dbTypes);
  }
  // BRANCH F — TITLE-SEARCH.
  if (titleSearchTerms && titleSearchTerms.length > 0) {
    return matchTitleSearch(product, slugCanonical, slug);
  }

  // No branch claims the slug (route would 404 or return empty).
  return false;
}
